// Native-shell (Capacitor) integration seam for the web app.
//
// The SAME bundle serves two worlds:
//   • Web (chat.poneva.com): API_BASE is '' → same-origin fetches, session is
//     the httpOnly cookie, and everything in this module no-ops.
//   • Native shells (iOS/Android): API_BASE is the absolute prod origin
//     (esbuild define, see scripts/build-ui.mjs --mobile). The WebView origin
//     (capacitor://localhost / https://localhost) is cross-site, so SameSite
//     cookies never flow — auth is the `session_token` from login/signup sent
//     as an Authorization: Bearer header, persisted in Capacitor Preferences
//     (WebView localStorage is OS-evictable; Preferences is not).
//
// Capacitor is reached through the `window.Capacitor` global the native bridge
// injects — deliberately NOT an npm import, so the web bundle carries zero
// Capacitor code and the service keeps zero Capacitor dependencies.

declare const __PONEVA_CHAT_API_BASE__: string;

/** Absolute API origin for the native shells; '' (same-origin) on the web. */
export const API_BASE: string =
  typeof __PONEVA_CHAT_API_BASE__ === 'string' ? __PONEVA_CHAT_API_BASE__ : '';

/** Prefix a server path with the API base (identity on the web). */
export const apiUrl = (path: string): string => `${API_BASE}${path}`;

/**
 * WebSocket URL for a server path. On the web it is the page's own host
 * (`wss:` on https, `ws:` on http), so the session cookie flows. In the native
 * shells it is the API origin with its scheme swapped (https → wss); the
 * shells authenticate the socket with a single-use ticket, never the cookie.
 */
export function wsUrl(path: string): string {
  if (API_BASE) return `${API_BASE.replace(/^http/u, 'ws')}${path}`;
  const { protocol, host } = globalThis.location;
  return `${protocol === 'https:' ? 'wss' : 'ws'}://${host}${path}`;
}

interface CapacitorBridge {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  registerPlugin?: <T>(name: string) => T;
}

interface PreferencesPlugin {
  get(opts: { key: string }): Promise<{ value: string | null }>;
  set(opts: { key: string; value: string }): Promise<void>;
  remove(opts: { key: string }): Promise<void>;
}

const bridge = (): CapacitorBridge | undefined =>
  (globalThis as { Capacitor?: CapacitorBridge }).Capacitor;

/** True when running inside the Capacitor WebView (iOS/Android app). */
export const isNativeShell = (): boolean => Boolean(bridge()?.isNativePlatform?.());

/** The current native platform, or null in the ordinary web app. */
export const nativePlatform = (): string | null =>
  isNativeShell() ? (bridge()?.getPlatform?.() ?? null) : null;

/** Register a Capacitor plugin without bundling native code into the web build. */
export function nativePlugin<T>(name: string): T | null {
  const cap = bridge();
  return cap?.isNativePlatform?.() && cap.registerPlugin
    ? (cap.registerPlugin<T>(name) ?? null)
    : null;
}

const TOKEN_KEY = 'poneva.sessionToken';
let prefsPlugin: PreferencesPlugin | null | undefined;
let sessionToken: string | null = null;
// Stored-token writes run one at a time, in order, so a slow login write never lands after a later clear.
let persisting: Promise<void> = Promise.resolve();

function preferences(): PreferencesPlugin | null {
  if (prefsPlugin !== undefined) return prefsPlugin;
  prefsPlugin = nativePlugin<PreferencesPlugin>('Preferences');
  return prefsPlugin;
}

/**
 * Load the persisted session token into memory. Call once at boot, before the
 * first authenticated request. No-op on the web.
 */
export async function initSessionToken(): Promise<void> {
  if (!isNativeShell()) return;
  try {
    const prefs = preferences();
    if (prefs) {
      // An empty value is a cleared token (see clearSessionToken).
      sessionToken = (await prefs.get({ key: TOKEN_KEY })).value || null;
      return;
    }
  } catch {
    /* plugin missing/failed — fall back below */
  }
  try {
    sessionToken = localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    sessionToken = null;
  }
}

/**
 * Adopt (or clear, with null) the session token from a login/signup response.
 * No-op on the web, where the httpOnly cookie is the session.
 */
export function setSessionToken(token: string | null): void {
  if (!isNativeShell()) return;
  sessionToken = token;
  persisting = persisting.then(() => persistToken(token));
}

/** The stored token could not be removed, so the next start of the app could restore it. */
export class SessionTokenKeptError extends Error {
  constructor() {
    super('session_token_kept');
    this.name = 'SessionTokenKeptError';
  }
}

/**
 * Log this device out of the shell: the token is forgotten at once, then removed from storage once any earlier
 * write has finished. Rejects with SessionTokenKeptError when a stored copy could survive a restart. No-op on the web.
 */
export async function clearSessionToken(): Promise<void> {
  if (!isNativeShell()) return;
  sessionToken = null;
  const cleared = persisting.then(removeStoredToken);
  persisting = cleared.catch(() => {});
  await cleared;
}

async function removeStoredToken(): Promise<void> {
  let kept = false;
  const prefs = preferences();
  if (prefs)
    try {
      await prefs.remove({ key: TOKEN_KEY });
    } catch {
      // An empty value restores no session either.
      await prefs.set({ key: TOKEN_KEY, value: '' }).catch(() => {
        kept = true;
      });
    }
  // Read at start when Preferences is missing or fails, so it is cleared too. It counts as gone only when the
  // removal ran or a read shows nothing there: storage unreadable now may be readable at the next start.
  let removed = true;
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    removed = false;
  }
  try {
    if (localStorage.getItem(TOKEN_KEY)) kept = true;
  } catch {
    if (!removed) kept = true;
  }
  if (kept) throw new SessionTokenKeptError();
}

async function persistToken(token: string | null): Promise<void> {
  try {
    const prefs = preferences();
    if (prefs) {
      if (token) await prefs.set({ key: TOKEN_KEY, value: token });
      else await prefs.remove({ key: TOKEN_KEY });
      return;
    }
  } catch {
    /* fall back below */
  }
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* best effort — worst case is a re-login */
  }
}

/** Authorization header carrying the shell session; {} on the web. */
export function authHeaders(): Record<string, string> {
  return sessionToken ? { authorization: `Bearer ${sessionToken}` } : {};
}
